/**
 * =============================================================================
 * useAdminRole HOOK - Admin Role Detection
 * =============================================================================
 * 
 * A custom React hook that checks if the current user has admin privileges.
 * Uses Supabase RPC function 'has_role' to verify the role server-side.
 * 
 * USAGE:
 * ```tsx
 * const { isAdmin, loading } = useAdminRole();
 * 
 * if (loading) return <Spinner />;
 * if (!isAdmin) return <AccessDenied />;
 * return <AdminPanel />;
 * ```
 * 
 * SECURITY:
 * - Role checking is performed server-side via RPC
 * - Client-side isAdmin flag is for UI purposes only
 * - All admin operations are protected by RLS policies
 * 
 * EXTENDING:
 * To add support for other roles (e.g., moderator):
 * 1. Create a new hook: useModeratorRole
 * 2. Or extend this hook to accept a role parameter
 * 
 * @returns {Object} { isAdmin: boolean, loading: boolean }
 * 
 * @see src/components/Header.tsx - Uses this hook for admin menu visibility
 * @see src/pages/AdminDashboard.tsx - Uses this hook for access control
 * =============================================================================
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// MODULE-LEVEL CACHE
// =============================================================================
// Without this, every admin page (and the Header) fires its own getSession()
// + has_role() round-trip on each mount and on every auth event
// (TOKEN_REFRESHED, INITIAL_SESSION...), which made the admin pages show the
// spinner for several seconds and sometimes flash "Åtkomst nekad".

type CacheEntry = { userId: string | null; isAdmin: boolean; isVerifiedAdmin: boolean };

let cache: CacheEntry | null = null;
let inFlight: Promise<CacheEntry> | null = null;
const listeners = new Set<(entry: CacheEntry) => void>();

const notify = (entry: CacheEntry) => {
  cache = entry;
  listeners.forEach((l) => l(entry));
};

const fetchAdminRole = (): Promise<CacheEntry> => {
  if (inFlight) return inFlight;

  inFlight = (async (): Promise<CacheEntry> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.user) return { userId: null, isAdmin: false, isVerifiedAdmin: false };

      // Two separate checks:
      // - has_admin_role_membership: is the account an admin at all (UI visibility)
      // - has_role('admin'): admin AND an active, OTP-verified admin session
      const [membershipRes, verifiedRes] = await Promise.all([
        supabase.rpc('has_admin_role_membership', { _user_id: session.user.id }),
        supabase.rpc('has_role', { _user_id: session.user.id, _role: 'admin' }),
      ]);

      if (membershipRes.error) {
        console.error('Error checking admin role:', membershipRes.error);
        return { userId: session.user.id, isAdmin: false, isVerifiedAdmin: false };
      }

      return {
        userId: session.user.id,
        isAdmin: membershipRes.data === true,
        isVerifiedAdmin: verifiedRes.data === true,
      };
    } catch (err) {
      console.error('Error checking admin role:', err);
      return { userId: null, isAdmin: false, isVerifiedAdmin: false };
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

// Single global auth subscription instead of one per hook instance.
supabase.auth.onAuthStateChange((event, session) => {
  // Only meaningful identity changes should invalidate the cache.
  if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') return;

  if (event === 'SIGNED_OUT' || !session?.user) {
    notify({ userId: null, isAdmin: false, isVerifiedAdmin: false });
    return;
  }

  if (cache && cache.userId === session.user.id) return;

  // Defer: never call Supabase synchronously inside the auth callback.
  setTimeout(() => {
    fetchAdminRole().then(notify);
  }, 0);
});

// =============================================================================
// HOOK DEFINITION
// =============================================================================

/**
 * Hook to check if the current authenticated user has the 'admin' role.
 * Result is cached across components and route changes.
 */
export const useAdminRole = () => {
  const [isAdmin, setIsAdmin] = useState(cache?.isAdmin ?? false);
  const [isVerifiedAdmin, setIsVerifiedAdmin] = useState(cache?.isVerifiedAdmin ?? false);
  const [loading, setLoading] = useState(cache === null);

  useEffect(() => {
    let active = true;

    const onUpdate = (entry: CacheEntry) => {
      if (!active) return;
      setIsAdmin(entry.isAdmin);
      setIsVerifiedAdmin(entry.isVerifiedAdmin);
      setLoading(false);
    };

    listeners.add(onUpdate);

    if (cache) {
      setIsAdmin(cache.isAdmin);
      setIsVerifiedAdmin(cache.isVerifiedAdmin);
      setLoading(false);
    } else {
      fetchAdminRole().then(notify);
    }

    return () => {
      active = false;
      listeners.delete(onUpdate);
    };
  }, []);

  return {
    /** True if the account has the admin role (used for menu visibility) */
    isAdmin,
    /** True only when the admin also has an active, OTP-verified admin session */
    isVerifiedAdmin,
    /** Force a fresh role/session check (call after verifying an admin OTP) */
    refresh: async () => {
      const entry = await fetchAdminRole();
      notify(entry);
      return entry;
    },
    /** True while role check is in progress */
    loading,
  };
};
